import React, { memo, type ReactElement } from 'react';

import type { NewCoreIconProps } from '@atlaskit/icon/types';
import PersonAvatarIcon from '@atlaskit/icon/core/person-avatar';
import HomeIcon from '@atlaskit/icon/core/home';
import OfficeBuildingIcon from '@atlaskit/icon/core/office-building';
import ClockIcon from '@atlaskit/icon/core/clock';
import StarUnstarredIcon from '@atlaskit/icon/core/star-unstarred';
import GlobeIcon from '@atlaskit/icon/core/globe';
import AppsIcon from '@atlaskit/icon/core/apps';
import GoalIcon from '@atlaskit/icon/core/goal';
import HashtagIcon from '@atlaskit/icon/core/hashtag';
import TeamsIcon from '@atlaskit/icon/core/teams';
import EditIcon from '@atlaskit/icon/core/edit';
import TaskIcon from '@atlaskit/icon/core/task';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import FilesIcon from '@atlaskit/icon/core/files';
import CustomizeIcon from '@atlaskit/icon/core/customize';
import SpacesIcon from '@atlaskit/icon-lab/core/spaces';
import type { LogoProps } from '@atlaskit/logo';
import { HubIcon, JiraIcon, LoomIcon, TeamsIcon as TeamsAppIcon } from '@atlaskit/logo';

import { expVal } from '@confluence/feature-experiments';

import type { MenuId } from '../PlatformNavigation/PlatformNavigationTypes.ts';

type StyledIconComponentProps = {
	icon: React.ComponentType<NewCoreIconProps>;
};

type StyledLogoComponentProps = {
	logo: React.ComponentType<LogoProps>;
	testId?: string;
};

const StyledIconComponent = memo(({ icon }: StyledIconComponentProps) => {
	const IconComponent = icon;
	return <IconComponent label="" color="currentColor" spacing="spacious" />;
});

const StyledLogoComponent = memo(({ logo, testId }: StyledLogoComponentProps) => {
	const LogoComponent = logo;
	return <LogoComponent label="" size="xsmall" shouldUseNewLogoDesign testId={testId} />;
});

//Note DST is planning to simplify how icons are passed to Nav 4 icons, & this boilerplate may not always be needed
//these are not behind loadables to prevent a delay in showing the icons. If there is performance regression they could be put behind loadables
export const ForYouIconComponent: React.JSX.Element = (
	<StyledIconComponent icon={PersonAvatarIcon} />
);
export const HomeIconComponent: React.JSX.Element = <StyledIconComponent icon={HomeIcon} />;
export const OfficeIconComponent: React.JSX.Element = (
	<StyledIconComponent icon={OfficeBuildingIcon} />
);
export const ClockIconComponent: React.JSX.Element = <StyledIconComponent icon={ClockIcon} />;
export const StarIconComponent: React.JSX.Element = (
	<StyledIconComponent icon={StarUnstarredIcon} />
);
export const GlobeIconComponent: React.JSX.Element = <StyledIconComponent icon={GlobeIcon} />;
export const SpacesIconComponent: React.JSX.Element = <StyledIconComponent icon={SpacesIcon} />;
export const AppsIconComponent: React.JSX.Element = <StyledIconComponent icon={AppsIcon} />;
export const GoalIconComponent: React.JSX.Element = <StyledIconComponent icon={GoalIcon} />;
export const HashIconComponent: React.JSX.Element = <StyledIconComponent icon={HashtagIcon} />;
export const TeamsIconComponent: React.JSX.Element = <StyledIconComponent icon={TeamsIcon} />;
export const EditIconComponent: React.JSX.Element = <StyledIconComponent icon={EditIcon} />;
export const TaskIconComponent: React.JSX.Element = <StyledIconComponent icon={TaskIcon} />;
export const TemplatesIconComponent: React.JSX.Element = <StyledIconComponent icon={FilesIcon} />;
export const MoreIconComponent: React.JSX.Element = (
	<StyledIconComponent icon={ShowMoreHorizontalIcon} />
);
export const CustomizeIconComponent: React.JSX.Element = (
	<StyledIconComponent icon={CustomizeIcon} />
);
export const NewCompanyHubIconComponent: React.JSX.Element = <StyledLogoComponent logo={HubIcon} />;
export const NewTeamsIconComponent: React.JSX.Element = <StyledLogoComponent logo={TeamsAppIcon} />;
export const JiraIconComponent: React.JSX.Element = <StyledLogoComponent logo={JiraIcon} />;
export const LoomIconComponent: React.JSX.Element = <StyledLogoComponent logo={LoomIcon} />;

// ensure that keys all match the menuId type in useMenuItems
export const iconByMenuId: { [key in MenuId]: ReactElement<StyledIconComponentProps> } = {
	customHome: HomeIconComponent,
	yourWork: ForYouIconComponent, // Component has been updated to 'For you', key remains the same.
	companyHub: OfficeIconComponent,
	recent: ClockIconComponent,
	starred: StarIconComponent,
	spaces: GlobeIconComponent,
	apps: AppsIconComponent,
	goals: GoalIconComponent,
	topics: HashIconComponent,
	teams: TeamsIconComponent,
	drafts: EditIconComponent,
	tasks: TaskIconComponent,
	templates: TemplatesIconComponent,
	customize: CustomizeIconComponent,
	companyHubShortcut: NewCompanyHubIconComponent,
	teamsShortcut: NewTeamsIconComponent,
	jiraShortcut: JiraIconComponent,
	loomShortcut: LoomIconComponent,
};

export const useIconByMenuId = (): {
	spaces: React.JSX.Element;
	starred: React.ReactElement<StyledIconComponentProps, string | React.JSXElementConstructor<any>>;
	yourWork: React.ReactElement<StyledIconComponentProps, string | React.JSXElementConstructor<any>>;
	recent: React.ReactElement<StyledIconComponentProps, string | React.JSXElementConstructor<any>>;
	jiraShortcut: React.ReactElement<
		StyledIconComponentProps,
		string | React.JSXElementConstructor<any>
	>;
	loomShortcut: React.ReactElement<
		StyledIconComponentProps,
		string | React.JSXElementConstructor<any>
	>;
	teamsShortcut: React.ReactElement<
		StyledIconComponentProps,
		string | React.JSXElementConstructor<any>
	>;
	companyHubShortcut: React.ReactElement<
		StyledIconComponentProps,
		string | React.JSXElementConstructor<any>
	>;
	customHome: React.ReactElement<
		StyledIconComponentProps,
		string | React.JSXElementConstructor<any>
	>;
	companyHub: React.ReactElement<
		StyledIconComponentProps,
		string | React.JSXElementConstructor<any>
	>;
	apps: React.ReactElement<StyledIconComponentProps, string | React.JSXElementConstructor<any>>;
	goals: React.ReactElement<StyledIconComponentProps, string | React.JSXElementConstructor<any>>;
	topics: React.ReactElement<StyledIconComponentProps, string | React.JSXElementConstructor<any>>;
	teams: React.ReactElement<StyledIconComponentProps, string | React.JSXElementConstructor<any>>;
	drafts: React.ReactElement<StyledIconComponentProps, string | React.JSXElementConstructor<any>>;
	tasks: React.ReactElement<StyledIconComponentProps, string | React.JSXElementConstructor<any>>;
	templates: React.ReactElement<
		StyledIconComponentProps,
		string | React.JSXElementConstructor<any>
	>;
	customize: React.ReactElement<
		StyledIconComponentProps,
		string | React.JSXElementConstructor<any>
	>;
} => {
	if (expVal('confluence_space_icon_update', 'isEnabled', false)) {
		return { ...iconByMenuId, spaces: SpacesIconComponent };
	}

	return iconByMenuId;
};
